import {useUserContext} from './UserContext'
import type {PropsWithChildren} from 'react'
import {createContext, useCallback, useContext, useState} from 'react'
import type {CustomParameters} from 'firebase/auth'
import {signOut as firebaseSignOut} from 'firebase/auth'
import {GoSchoolDialog} from '@goschool/mui'
import {SignInPage} from './SignInPage'
import {SignUpPage} from './SignUpPage'
import {PasswordResetPage} from './PasswordResetPage'
import {useCustomizationConfig} from '@goschool/model'


type AuthenticationMode = 'signIn' | 'signUp' | 'resetPassword'
export type AuthProviderId = 'google' | 'microsoft' | 'credentials' | 'bme-edai'

export interface AuthenticationContextType {
  authenticationMode: AuthenticationMode | null;

  enabledMethods: AuthProviderId[];
  signIn: () => void;
  signUp: () => void;
  signOut: () => void;
  resetPassword: () => void;

  hideAuthentication: () => void;
  customConfigs: Record<string, CustomParameters>;
}

export const AuthenticationContext = createContext<AuthenticationContextType | undefined>(undefined)


export interface AuthenticationContextProviderProps extends PropsWithChildren {
  enabledMethods: AuthProviderId[];
  defaultMode?: AuthenticationMode;
  customConfigs: Record<string, CustomParameters>;
}

export interface AuthenticationDialogProps {
  authenticationMode: 'signIn' | 'signUp' | 'resetPassword' | null,
  onClose: () => void
}

export function DefaultAuthenticationDialog(props: AuthenticationDialogProps) {
  return <GoSchoolDialog open={props.authenticationMode != null} onClose={props.onClose} fullWidth={true}>{
    props.authenticationMode === 'signIn'
      ? <SignInPage/>
      : props.authenticationMode === 'signUp'
        ? <SignUpPage/>
        : <PasswordResetPage/>
  }</GoSchoolDialog>
}

export function AuthenticationContextProvider(
  {
    enabledMethods, defaultMode, children, customConfigs
  }: AuthenticationContextProviderProps) {
  const {auth} = useUserContext()

  const [authenticationMode, setAuthenticationMode] = useState<AuthenticationMode | null>(
    defaultMode ?? null
  )
  const signIn = useCallback(() => setAuthenticationMode('signIn'), [])
  const signUp = useCallback(() => setAuthenticationMode('signUp'), [])
  const resetPassword = useCallback(() => setAuthenticationMode('resetPassword'), [])
  const hideAuthentication = useCallback(() => setAuthenticationMode(null), [])
  const signOut = useCallback(() => {
    firebaseSignOut(auth)
  }, [auth])

  const {AuthenticationDialog} = useCustomizationConfig()

  const AuthDialog = AuthenticationDialog ?? DefaultAuthenticationDialog
  
  return <AuthenticationContext.Provider
    value={{
      enabledMethods, authenticationMode, signIn, signUp, resetPassword, hideAuthentication, signOut, customConfigs
    }}>
    {children}
    <AuthDialog authenticationMode={authenticationMode} onClose={hideAuthentication}/>
  </AuthenticationContext.Provider>
}


export function useAuthenticationContext() {
  const context = useContext(AuthenticationContext)
  if (context == null) {
    throw new Error('useAuthenticationContext must be used within a AuthenticationContextProvider')
  }
  return context
}
